import { useMutation } from "@tanstack/react-query";
import { api } from "../../api";
import { EntityType, type EntityRecordPatch } from "../../generated/core-api";
import type { ComponentResolutionMap } from "../utils/buildImportRows";
import {
  stampPayComponentAmbiguityResolutions,
  type HeldPayComponentRow,
} from "../utils/payComponentEntityPatch";

export interface PatchPayComponentAmbiguityArgs {
  /** Workload the held `pay_components` entity record belongs to. */
  workloadId: string;
  /**
   * The full set of currently-held canonical rows — read via
   * `GET …/workloads/{workloadId}/entities/pay_components`
   * (`EntityRecordView.canonical_entities`) — to carry forward. Never a
   * subset: `EntityRecordPatch.canonical_entities` is a full-list replace.
   */
  heldRows: HeldPayComponentRow[];
  /** Component name → chosen `companyComponentId`, from `WizardState.componentResolutions` via `buildComponentResolutionMap`. */
  resolutions: ComponentResolutionMap;
}

const PAY_COMPONENTS_ENTITY_TYPE = EntityType.pay_components;

/**
 * PATCH held `pay_components` entity rows to stamp `companyComponentId` after
 * ambiguity resolution in Map Columns (LADR-039 #440 Step 11, W3b) —
 * `PATCH …/workloads/{workloadId}/entities/pay_components`.
 *
 * This vertical is **hybrid** (see `.plans/ladr-039-contract-mapping.md`'s pay
 * components section): only the durable-hold-and-stamp step here (plus the
 * eventual commit, Step 12) moves to workloads. `useImportPayComponents` (W3c
 * preflight) and `useSaveMappings`/`useCompanyMappings` (W3a) stay on
 * Christmas-direct endpoints, unchanged.
 *
 * Wired into the live cutover path via `usePayComponentImportWorkloadsManager`
 * when `EnableWorkloadsCutover` is on (`WizardProviderWorkloads`). Christmas
 * mapping save/preflight remain Track 1 (hybrid W3a/W3c).
 *
 * Local, one-off `useMutation` wrapping `patchOnboardingEntityRecord`. Sends
 * `If-Match` from the prior GET's ETag header (surfaced by
 * `customCoreInstance`).
 */
export function usePatchPayComponentAmbiguity() {
  return useMutation({
    mutationFn: async ({
      workloadId,
      heldRows,
      resolutions,
    }: PatchPayComponentAmbiguityArgs) => {
      const entityRecordPatch: EntityRecordPatch = {
        canonical_entities: stampPayComponentAmbiguityResolutions(
          heldRows,
          resolutions,
        ),
      };
      const current = (await api.getOnboardingEntityRecord(
        workloadId,
        PAY_COMPONENTS_ENTITY_TYPE,
      )) as Awaited<ReturnType<typeof api.getOnboardingEntityRecord>> & {
        etag?: string;
      };
      return api.patchOnboardingEntityRecord(
        workloadId,
        PAY_COMPONENTS_ENTITY_TYPE,
        entityRecordPatch,
        current.etag
          ? { headers: { "If-Match": current.etag } }
          : undefined,
      );
    },
  });
}
